import {
  CustomModalStyled,
  EduButton,
  EduIf,
  FieldLabel,
  NumberInputStyled,
  SelectInputStyled,
  notification,
} from '@edulastic/common'
import {
  MULTIPLE_CHOICE,
  SHORT_TEXT,
  TRUE_OR_FALSE,
} from '@edulastic/constants/const/questionType'
import { Col, Icon, Row, Select, Spin } from 'antd'
import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { segmentApi } from '@edulastic/api'
import AIMultiSelectItemTypeInfo from '@edulastic/common/src/components/AIMultiSelectItemTypeInfo'
import { selectsData } from '../../../TestPage/components/common'
import {
  fetchAIGeneratedQuestionAction,
  setAIGeneratedQuestionStateAction,
} from '../../../src/actions/aiGenerateQuestion'
import { ModalFooter, ModalWrapper } from '../../common/Modal'
import {
  QuestionFormWrapper,
  StyledBetaTag,
} from '../styled-components/QuestionForm'
import StandardSet from './QuestionEditModal/common/StandardSet'
import { StandardSelectWrapper } from '../styled-components/StandardSet'
import { validateStandardsData } from '../utils/common'
import {
  standardsFields,
  questionGenerationStatus,
  transcriptGenerationFailErrorCode,
} from '../constants'
import LoaderModal from './LoaderModal'
import InputTranscriptModal from './InputTranscriptModal'
import { getVideoDuration } from '../utils/videoPreviewHelpers'
import TranscriptGenerationFailureNotification from './TranscriptGenerationFailureNotification'
import { StyledRequired } from '../../../Reports/subPages/dataWarehouseReports/GoalsAndInterventions/common/components/Form/styled-components'

class AddBulkModal extends React.Component {
  static propTypes = {
    visible: PropTypes.bool.isRequired,
    minAvailableQuestionIndex: PropTypes.number.isRequired,
    onApply: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    questions: PropTypes.array.isRequired,
  }

  state = {
    number: 2,
    type: [MULTIPLE_CHOICE],
    generatedQuestionsCount: 0,
    authorDifficulty: '',
    depthOfKnowledge: '',
    alignment: [
      {
        curriculum: '',
        curriculumId: '',
        domains: [],
        grades: [],
        standards: [],
        subject: '',
      },
    ],
    userTranscript: '',
    showTranscriptInputModal: false,
  }

  setGeneratedQuestionsCount = (count) => {
    this.setState({
      generatedQuestionsCount: count,
    })
  }

  handleChange = (field) => (value) =>
    this.setState({
      [field]: value,
    })

  handleManualTranscriptModalClose = () => {
    this.setState({
      userTranscript: '',
      showTranscriptInputModal: false,
    })
  }

  handleApply = (aiQuestions) => {
    const {
      number,
      type,
      alignment,
      authorDifficulty,
      depthOfKnowledge,
    } = this.state

    const { onApply, minAvailableQuestionIndex } = this.props

    if (aiQuestions) {
      onApply({
        number,
        startingIndex: minAvailableQuestionIndex,
        alignment,
        authorDifficulty,
        depthOfKnowledge,
        aiQuestions,
        isFromAddBulk: true,
      })
    } else {
      this.generateViaAI({ questionCount: number, questionTypes: type })
      segmentApi.genericEventTrack('GenerateAIItem', {
        source: 'Video Quiz: Generate',
      })
    }
  }

  generateViaAI = ({ questionCount, questionTypes }) => {
    const {
      alignment,
      authorDifficulty,
      depthOfKnowledge,
      userTranscript,
    } = this.state
    const { questions, setAiQuestionsGenerationStatus, videoRef } = this.props

    const { grades = [], standards = [], subject = '', curriculum = '' } =
      alignment?.[0] || {}

    const { isValid, message } = validateStandardsData({
      grades,
      subject,
      curriculum,
      questionCount,
      questionTypes,
    })
    if (!isValid) {
      return notification({ type: 'warn', msg: message })
    }

    const curriculumStandardsIdsIdentifiers = []
    const curriculumStandardsDescriptions = []
    ;(standards || []).forEach(({ identifier = '', description = '' }) => {
      curriculumStandardsDescriptions.push({
        id: identifier,
        description,
      })
      curriculumStandardsIdsIdentifiers.push(identifier)
    })

    const { fetchAIGeneratedQuestion, videoUrl } = this.props
    setAiQuestionsGenerationStatus(questionGenerationStatus.IN_PROGRESS)
    fetchAIGeneratedQuestion({
      videoUrl,
      count: questionCount,
      questionTypes,
      difficultLevels: authorDifficulty?.length
        ? [authorDifficulty]
        : selectsData.allAuthorDifficulty
            .map((difficulty) => difficulty?.value || '')
            .filter((difficultyValue) => difficultyValue),
      depthsOfKnowledge: depthOfKnowledge?.length
        ? [depthOfKnowledge]
        : selectsData.allDepthOfKnowledgeAI
            .map((dok) => dok?.value || '')
            .filter((dokValue) => dokValue),
      grades,
      subject,
      ...(curriculum?.length ? { standardSet: curriculum } : {}),
      commonCoreStandards: curriculumStandardsIdsIdentifiers,
      commonCoresStandardDescriptions: curriculumStandardsDescriptions,
      existingQuestions: questions
        ?.map(({ stimulus, questionDisplayTimestamp }) => ({
          name: stimulus,
          ...(questionDisplayTimestamp
            ? { displayAtSecond: questionDisplayTimestamp }
            : {}),
        }))
        .filter(({ name }) => name),
      userTranscript,
      videoDuration: getVideoDuration(videoRef),
    })
  }

  trackManualTranscriptEvent = ({ action }) => {
    segmentApi.genericEventTrack('VideoQuiz:ManualTranscriptInput', {
      action,
    })
  }

  componentDidUpdate = (prevProps) => {
    const {
      aiGenerateQuestionState: { result, apiStatus, errorCode } = {},
      setAIGeneratedQuestionState,
      setAiQuestionsGenerationStatus,
    } = this.props

    if (prevProps?.aiGenerateQuestionState?.errorCode !== errorCode) {
      if (errorCode === transcriptGenerationFailErrorCode) {
        this.setState({
          transcriptGenFailureNotificationOpen: true,
        })
      } else {
        this.handleManualTranscriptModalClose()
      }
    }

    if (
      (result || []).length > 0 &&
      ['SUCCESS', 'FAILED'].includes(apiStatus)
    ) {
      setAiQuestionsGenerationStatus(
        apiStatus === 'SUCCESS' && result?.length > 0
          ? questionGenerationStatus.SUCCESS
          : questionGenerationStatus.INITIAL
      )
      this.setGeneratedQuestionsCount(result?.length || 0)
      this.handleApply(result || [])
      setAIGeneratedQuestionState({
        apiStatus: false,
        result: [],
      })
    }

    if (
      prevProps?.aiGenerateQuestionState?.apiStatus !== apiStatus &&
      apiStatus === 'FAILED'
    ) {
      setAiQuestionsGenerationStatus(questionGenerationStatus.INITIAL)
      this.setGeneratedQuestionsCount(0)
    }
  }

  componentDidMount = () => {
    const {
      aiGenerateQuestionState: { apiStatus } = {},
      setAIGeneratedQuestionState,
    } = this.props
    if (apiStatus) {
      setAIGeneratedQuestionState({
        apiStatus: false,
        result: [],
      })
    }
  }

  render() {
    const {
      number,
      type,
      alignment,
      authorDifficulty,
      depthOfKnowledge,
      generatedQuestionsCount,
      showTranscriptInputModal,
      transcriptGenFailureNotificationOpen,
      userTranscript,
    } = this.state
    const {
      onCancel,
      visible,
      aiGenerateQuestionState: { apiStatus } = {},
      aiQuestionsGenerationStatus,
      setAiQuestionsGenerationStatus,
    } = this.props

    const loading = apiStatus === 'INITIATED'

    return (
      <>
        <LoaderModal
          status={aiQuestionsGenerationStatus}
          generatedQuestionsCount={generatedQuestionsCount}
          setAiQuestionsGenerationStatus={setAiQuestionsGenerationStatus}
        />
        {showTranscriptInputModal || transcriptGenFailureNotificationOpen ? (
          <>
            <InputTranscriptModal
              visible={
                showTranscriptInputModal &&
                visible &&
                aiQuestionsGenerationStatus === questionGenerationStatus.INITIAL
              }
              transcriptText={userTranscript}
              onChange={this.handleChange('userTranscript')}
              onCancel={this.handleManualTranscriptModalClose}
              disabled={loading || !userTranscript}
              onSubmit={() => {
                this.handleApply()
                this.trackManualTranscriptEvent({
                  action: 'generate',
                })
              }}
            />
            <TranscriptGenerationFailureNotification
              visible={transcriptGenFailureNotificationOpen}
              onCancel={() => {
                this.setState({ transcriptGenFailureNotificationOpen: false })
                onCancel()
                this.trackManualTranscriptEvent({
                  action: 'cancel',
                })
              }}
              onProceed={() => {
                this.setState({
                  showTranscriptInputModal: true,
                  transcriptGenFailureNotificationOpen: false,
                })
                this.trackManualTranscriptEvent({
                  action: 'proceed',
                })
              }}
            />
          </>
        ) : (
          <CustomModalStyled
            visible={
              visible &&
              aiQuestionsGenerationStatus === questionGenerationStatus.INITIAL
            }
            title={
              <>
                Auto Generate{' '}
                <StyledBetaTag alignItems="left">BETA</StyledBetaTag>
              </>
            }
            onCancel={onCancel}
            maskClosable={false}
            footer={[
              <ModalFooter marginTop="35px">
                <EduButton isGhost onClick={onCancel} data-cy="aiCancelButton">
                  Cancel
                </EduButton>
                <EduButton
                  disabled={loading}
                  onClick={() => this.handleApply()}
                  data-cy="aiGenerateButton"
                >
                  Generate Questions
                  <EduIf condition={loading}>
                    <Spin size="small" indicator={<Icon type="loading" />} />
                  </EduIf>
                </EduButton>
              </ModalFooter>,
            ]}
          >
            <ModalWrapper width="100%">
              <QuestionFormWrapper>
                <Row gutter={20}>
                  <Col span={24}>
                    <FieldLabel>
                      Number Of Items
                      <StyledRequired>*</StyledRequired>
                    </FieldLabel>
                    <NumberInputStyled
                      min={1}
                      max={10}
                      value={number}
                      onChange={this.handleChange('number')}
                      data-cy="questionCount"
                    />
                  </Col>
                </Row>
                <Row style={{ marginTop: '30px' }} gutter={20}>
                  <Col span={24}>
                    <FieldLabel>
                      Item Types
                      <StyledRequired>*</StyledRequired>
                    </FieldLabel>
                    <SelectInputStyled
                      showArrow
                      showSearch
                      placeholder="Select Item Types"
                      height="36px"
                      value={type}
                      onChange={this.handleChange('type')}
                      getPopupContainer={(triggerNode) =>
                        triggerNode.parentNode
                      }
                      data-cy="questionTypes"
                      mode="multiple"
                      aria-label="Select item types for AI generation"
                      aria-describedby="video-quiz-ai-item-generation-item-types-description"
                      role="listbox"
                      aria-expanded={false}
                      aria-multiselectable="true"
                      tabIndex={0}
                    >
                      <Select.Option
                        data-cy="Multiple Choice"
                        value={MULTIPLE_CHOICE}
                        aria-label="Question type: Multiple Choice"
                      >
                        Multiple Choice
                      </Select.Option>
                      <Select.Option
                        data-cy="True or False"
                        value={TRUE_OR_FALSE}
                        aria-label="Question type: True or False"
                      >
                        True or False
                      </Select.Option>
                      <Select.Option
                        data-cy="Text Entry"
                        value={SHORT_TEXT}
                        aria-label="Question type: Text Entry"
                      >
                        Text Entry
                      </Select.Option>
                    </SelectInputStyled>
                    <div
                      id="video-quiz-ai-item-generation-item-types-description"
                      className="sr-only"
                    >
                      Select one or more item types for AI question generation.
                      AI will create questions based on your selection.
                    </div>
                  </Col>
                </Row>
                {type?.length > 1 && <AIMultiSelectItemTypeInfo />}
              </QuestionFormWrapper>
              <StandardSelectWrapper style={{ paddingRight: 'unset' }}>
                <StandardSet
                  alignment={alignment}
                  onUpdate={(data) =>
                    this.setState({ alignment: data.alignment })
                  }
                  isDocBased
                  showIconBrowserBtn
                  standardsRequiredFields={[
                    standardsFields.SUBJECT,
                    standardsFields.GRADES,
                    standardsFields.STANDARD_SET,
                  ]}
                  trackingEventSource="VQ"
                />
                <Row style={{ marginTop: '10px' }} gutter={20}>
                  <Col md={12}>
                    <FieldLabel>DOK</FieldLabel>
                    <SelectInputStyled
                      placeholder="Select DOK"
                      onSelect={(val) =>
                        this.setState({ depthOfKnowledge: val })
                      }
                      value={depthOfKnowledge}
                      getPopupContainer={(triggerNode) =>
                        triggerNode.parentNode
                      }
                      data-cy="aiDok"
                    >
                      <Select.Option key="Select DOK" value="">
                        Select DOK
                      </Select.Option>
                      {selectsData.allDepthOfKnowledgeAI.map(
                        (el) =>
                          el.value && (
                            <Select.Option key={el.value} value={el.value}>
                              {el.text}
                            </Select.Option>
                          )
                      )}
                    </SelectInputStyled>
                  </Col>
                  <Col md={12}>
                    <FieldLabel>Difficulty</FieldLabel>
                    <SelectInputStyled
                      placeholder="Select Difficulty Level"
                      onSelect={(val) =>
                        this.setState({ authorDifficulty: val })
                      }
                      value={authorDifficulty}
                      getPopupContainer={(triggerNode) =>
                        triggerNode.parentNode
                      }
                      data-cy="aiDifficulty"
                    >
                      <Select.Option key="Select Difficulty Level" value="">
                        Select Difficulty Level
                      </Select.Option>
                      {selectsData.allAuthorDifficulty.map(
                        (el) =>
                          el.value && (
                            <Select.Option key={el.value} value={el.value}>
                              {el.text}
                            </Select.Option>
                          )
                      )}
                    </SelectInputStyled>
                  </Col>
                </Row>
              </StandardSelectWrapper>
            </ModalWrapper>
          </CustomModalStyled>
        )}
      </>
    )
  }
}

const enhance = compose(
  connect(
    (state) => ({
      videoUrl: state.tests.entity.videoUrl,
      aiGenerateQuestionState: state.aiGenerateQuestionState,
    }),
    {
      setAIGeneratedQuestionState: setAIGeneratedQuestionStateAction,
      fetchAIGeneratedQuestion: fetchAIGeneratedQuestionAction,
    }
  )
)

export default enhance(AddBulkModal)
